import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  acceptWhatsappCall,
  clearWhatsappActiveCall,
  completeWhatsappCallRecording,
  connectWhatsappCall,
  createWhatsappCallTranscriptionToken,
  fetchWhatsappCallDetail,
  fetchWhatsappCallTranscript,
  fetchWhatsappCallingSettings,
  preAcceptWhatsappCall,
  rejectWhatsappCall,
  saveWhatsappCallTranscriptSegment,
  sendWhatsappCallPermissionRequest,
  startWhatsappCallRecording,
  terminateWhatsappCall,
  uploadWhatsappCallRecordingChunk,
} from 'src/redux/slices/whatsappV2';
import { updateError } from 'src/redux/slices/user';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import {
  STREAMING_CHUNK_MS,
  appendPcm16Buffer,
  downsampleToPcm16,
  extractTranscriptEvent,
  takePcm16Chunks,
} from 'src/utils/liveCallTranscription';
import useAuth from 'src/hooks/useAuth';
import { COLORS } from './styles';

const TERMINAL_STATUSES = new Set(['rejected', 'terminated', 'failed']);
const INCOMING_RING_STATUSES = new Set(['pending', 'connecting', 'ringing']);

const STATUS_LABELS = {
  pending: 'Pending',
  connecting: 'Connecting',
  ringing: 'Ringing',
  pre_accepted: 'Preparing',
  accepted: 'Connected',
  rejected: 'Declined',
  terminated: 'Ended',
  failed: 'Failed',
  permission_required: 'Permission required',
};

const normalizeWaId = (value) => String(value || '').replace(/[^\d]/g, '');

const buildClientCallId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (error) {
    // ignore and use the deterministic fallback below
  }
  return `wacall-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getCallingConfig = (settings) => settings?.calling ?? settings ?? {};

const getRecordingMimeType = () => {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
  ];
  if (typeof window === 'undefined' || !window.MediaRecorder?.isTypeSupported) return '';
  return candidates.find((candidate) => window.MediaRecorder.isTypeSupported(candidate)) || '';
};

const formatDuration = (ms) => {
  const totalSeconds = Math.max(0, Math.floor((ms || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const getSpeakerLabel = (speaker, patientLabel = 'Patient') => {
  if (speaker === 'agent') return 'You';
  if (speaker === 'patient') return patientLabel;
  return 'Call';
};

const getResponseError = (response, fallback) => (
  response?.data?.detail
  || response?.data?.error
  || response?.data?.message
  || fallback
);

const EMPTY_TRANSCRIPT_ROWS = Object.freeze([]);

// navigator.mediaDevices rejects with a DOMException whose .message is the
// browser's own raw wording — Chrome says "Requested device not found" for
// NotFoundError. None of these are application faults; every one is something
// the person at the keyboard can fix, so say what that is.
export const MEDIA_DEVICE_ADVICE = {
    NotFoundError: 'No microphone was found. Connect one and try the call again.',
    DevicesNotFoundError: 'No microphone was found. Connect one and try the call again.',
    NotAllowedError: 'Microphone access is blocked. Allow it from the icon in your browser address bar, then try again.',
    PermissionDeniedError: 'Microphone access is blocked. Allow it from the icon in your browser address bar, then try again.',
    NotReadableError: 'Your microphone is in use by another application. Close that app and try again.',
    TrackStartError: 'Your microphone is in use by another application. Close that app and try again.',
    OverconstrainedError: 'The selected microphone is unavailable. Choose a different input device in your system sound settings.',
    SecurityError: 'Microphone access needs a secure connection. Reload the page over https and try again.',
    AbortError: 'The microphone could not be started. Try again, or restart your browser.',
};

export function describeMediaDeviceError(error) {
    if (!error) return null;
    return MEDIA_DEVICE_ADVICE[error.name] || null;
}

export default function WhatsappCallControls({ contact, disabled, iconSx }) {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const audioRef = React.useRef(null);
  const peerRef = React.useRef(null);
  const localStreamRef = React.useRef(null);
  const remoteStreamRef = React.useRef(null);
  const remoteDescriptionRef = React.useRef({});
  const recordingRef = React.useRef({
    recorder: null,
    chunks: [],
    sequence: 0,
    callId: null,
    recordingId: null,
    mimeType: '',
    startedAt: null,
    stopping: false,
  });
  const recordingAudioRef = React.useRef({
    context: null,
    destination: null,
    sources: {},
  });
  const transcriberRefs = React.useRef({});
  const lastActiveCallIdRef = React.useRef(null);
  const [localError, setLocalError] = React.useState('');
  const [permissionRequired, setPermissionRequired] = React.useState(false);
  const [micActive, setMicActive] = React.useState(false);
  const [recordingState, setRecordingState] = React.useState('idle');
  const [recordingElapsedMs, setRecordingElapsedMs] = React.useState(0);
  const [recordingError, setRecordingError] = React.useState('');
  const [showTranscript, setShowTranscript] = React.useState(false);
  const [liveTranscript, setLiveTranscript] = React.useState([]);
  const [partialTranscript, setPartialTranscript] = React.useState({});

  const {
    practiceId,
    currentBusinessId,
    currentWhatsappPhoneId,
    callingSettings,
    callingSettingsLoading,
    whatsappCallActionLoading,
    whatsappCallError,
    activeCall,
    persistedTranscript,
  } = useSelector(
    (state) => {
      const activeCallId = state?.whatsappV2?.whatsappActiveCallId;
      return {
        practiceId: state?.practiceTreatmentList?.practiceId,
        currentBusinessId: state?.whatsappV2?.currentBusinessId,
        currentWhatsappPhoneId: state?.whatsappV2?.currentWhatsappPhoneId,
        callingSettings: state?.whatsappV2?.callingSettings,
        callingSettingsLoading: state?.whatsappV2?.callingSettingsLoading,
        whatsappCallActionLoading: state?.whatsappV2?.whatsappCallActionLoading,
        whatsappCallError: state?.whatsappV2?.whatsappCallError,
        activeCall: activeCallId ? state?.whatsappV2?.whatsappCallsById?.[activeCallId] : null,
        persistedTranscript: activeCallId ? state?.whatsappV2?.whatsappCallTranscriptByCallId?.[activeCallId] ?? EMPTY_TRANSCRIPT_ROWS : EMPTY_TRANSCRIPT_ROWS,
      };
    },
    shallowEqual
  );

  // hasFeatureAccess reads the store directly, so subscribe here to re-check
  // when permissions arrive after this component has mounted.
  useSelector((state) => state?.permissions, shallowEqual);

  const contactWaId = React.useMemo(() => normalizeWaId(contact?.phoneNumber), [contact?.phoneNumber]);
  const callingConfig = getCallingConfig(callingSettings);
  const callingStatus = String(callingConfig?.status || '').toLowerCase();
  const isCallingConfiguredOff = Boolean(callingStatus && callingStatus !== 'enabled');
  const isSuperuser = Boolean(user?.is_superuser);
  const canUseCalling = isSuperuser || hasFeatureAccess('whatsapp-calling', practiceId);
  const hasWebRtc = typeof window !== 'undefined' && Boolean(window.RTCPeerConnection && navigator?.mediaDevices?.getUserMedia);
  const hasRecordingSupport = typeof window !== 'undefined' && Boolean(window.MediaRecorder && (window.AudioContext || window.webkitAudioContext));
  const activeStatus = activeCall?.status;
  const hasActiveCall = Boolean(activeCall?.id && !TERMINAL_STATUSES.has(activeStatus));
  const isIncomingCall = activeCall?.direction === 'inbound' && INCOMING_RING_STATUSES.has(activeStatus);
  const isRecording = recordingState === 'recording';
  const transcriptRows = React.useMemo(() => {
    const rows = [...(persistedTranscript || []), ...liveTranscript]
      .filter((item) => item?.text)
      .reduce((acc, item) => {
        const key = item.provider_event_id || item.providerEventId || item.id || `${item.speaker}-${item.text}`;
        acc.set(key, item);
        return acc;
      }, new Map());
    return Array.from(rows.values()).slice(-8);
  }, [liveTranscript, persistedTranscript]);
  const buttonDisabled = (
    disabled
    || !canUseCalling
    || !contactWaId
    || isCallingConfiguredOff
    || whatsappCallActionLoading
    || callingSettingsLoading
    || hasActiveCall
  );

  const resetRecordingUi = React.useCallback(() => {
    setRecordingState('idle');
    setRecordingElapsedMs(0);
    setRecordingError('');
    setLiveTranscript([]);
    setPartialTranscript({});
    setShowTranscript(false);
  }, []);

  const appendTranscript = React.useCallback((segment) => {
    setLiveTranscript((previous) => {
      const key = segment.provider_event_id || segment.providerEventId || segment.id || `${segment.speaker}-${segment.text}`;
      return [
        ...previous.filter((item) => (item.provider_event_id || item.providerEventId || item.id || `${item.speaker}-${item.text}`) !== key),
        segment,
      ].slice(-20);
    });
  }, []);

  const cleanupRecordingAudio = React.useCallback(() => {
    try {
      Object.values(recordingAudioRef.current.sources || {}).forEach((source) => {
        try {
          source.disconnect();
        } catch (error) {
          // ignore disconnected nodes
        }
      });
      recordingAudioRef.current.context?.close?.();
    } catch (error) {
      console.error('Error cleaning WhatsApp recording audio graph', error);
    } finally {
      recordingAudioRef.current = { context: null, destination: null, sources: {} };
    }
  }, []);

  const connectStreamToRecording = React.useCallback((stream, key) => {
    if (!stream || recordingAudioRef.current.sources?.[key]) return null;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!recordingAudioRef.current.context) {
      const context = new AudioContextClass();
      recordingAudioRef.current.context = context;
      recordingAudioRef.current.destination = context.createMediaStreamDestination();
      recordingAudioRef.current.sources = {};
    }
    const source = recordingAudioRef.current.context.createMediaStreamSource(stream);
    source.connect(recordingAudioRef.current.destination);
    recordingAudioRef.current.sources[key] = source;
    recordingAudioRef.current.context.resume?.().catch(() => {});
    return recordingAudioRef.current.destination.stream;
  }, []);

  const stopTranscriber = React.useCallback((speaker) => {
    const ref = transcriberRefs.current[speaker];
    if (!ref) return;
    try {
      ref.processor?.disconnect();
      ref.source?.disconnect();
      ref.silenceGain?.disconnect();
      ref.context?.close?.();
      if (ref.socket?.readyState === WebSocket.OPEN) {
        ref.socket.send(JSON.stringify({ type: 'ForceEndpoint' }));
        window.setTimeout(() => {
          if (ref.socket?.readyState === WebSocket.OPEN) {
            ref.socket.send(JSON.stringify({ type: 'Terminate' }));
          }
        }, 250);
        window.setTimeout(() => {
          if (ref.socket?.readyState !== WebSocket.CLOSED) ref.socket?.close?.();
        }, 1500);
      } else {
        ref.socket?.close?.();
      }
    } catch (error) {
      console.error('Error stopping WhatsApp live transcriber', error);
    } finally {
      delete transcriberRefs.current[speaker];
      setPartialTranscript((previous) => {
        const next = { ...previous };
        delete next[speaker];
        return next;
      });
    }
  }, []);

  const stopAllTranscribers = React.useCallback(() => {
    Object.keys(transcriberRefs.current || {}).forEach(stopTranscriber);
  }, [stopTranscriber]);

  const startTranscriber = React.useCallback(async (callId, stream, speaker, recordingId) => {
    if (!callId || !stream || transcriberRefs.current[speaker] || typeof WebSocket === 'undefined') return;
    try {
      const tokenResponse = await dispatch(createWhatsappCallTranscriptionToken(callId, speaker));
      if (!tokenResponse?.ok || !tokenResponse?.data?.token) {
        setRecordingError('Recording is on. Live transcript is unavailable.');
        return;
      }

      const {
        websocket_url: websocketUrl,
        token,
        sample_rate: sampleRate,
        encoding,
        speech_model: speechModel,
        format_turns: formatTurns,
      } = tokenResponse.data;
      const resolvedSampleRate = sampleRate || 16000;
      const query = new URLSearchParams({
        token,
        sample_rate: String(resolvedSampleRate),
        encoding: encoding || 'pcm_s16le',
        speech_model: speechModel || 'u3-rt-pro',
        mode: 'balanced',
        format_turns: String(formatTurns ?? true),
      });
      const socket = new WebSocket(`${websocketUrl}?${query.toString()}`);
      socket.binaryType = 'arraybuffer';

      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const context = new AudioContextClass();
      const source = context.createMediaStreamSource(stream);
      const processor = context.createScriptProcessor(4096, 1, 1);
      const silenceGain = context.createGain();
      silenceGain.gain.value = 0;
      const samplesPerChunk = Math.max(1, Math.round((resolvedSampleRate * STREAMING_CHUNK_MS) / 1000));
      let pendingPcm = new Int16Array(0);

      source.connect(processor);
      processor.connect(silenceGain);
      silenceGain.connect(context.destination);

      processor.onaudioprocess = (event) => {
        if (socket.readyState !== WebSocket.OPEN) return;
        const pcm = downsampleToPcm16(
          event.inputBuffer.getChannelData(0),
          context.sampleRate,
          resolvedSampleRate
        );
        pendingPcm = appendPcm16Buffer(pendingPcm, pcm);
        const { chunks, remaining } = takePcm16Chunks(pendingPcm, samplesPerChunk);
        pendingPcm = remaining;
        chunks.forEach((chunk) => socket.send(chunk));
      };

      socket.onmessage = (event) => {
        try {
          const transcriptEvent = extractTranscriptEvent(JSON.parse(event.data));
          if (!transcriptEvent) return;
          if (!transcriptEvent.isFinal) {
            setPartialTranscript((previous) => ({ ...previous, [speaker]: transcriptEvent.text }));
            return;
          }

          const segment = {
            recording_id: recordingId,
            speaker,
            text: transcriptEvent.text,
            is_final: true,
            started_at_ms: transcriptEvent.startedAtMs,
            ended_at_ms: transcriptEvent.endedAtMs,
            provider_event_id: transcriptEvent.providerEventId !== ''
              ? `${speaker}-${transcriptEvent.providerEventId}`
              : `${speaker}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          };
          setPartialTranscript((previous) => {
            const next = { ...previous };
            delete next[speaker];
            return next;
          });
          appendTranscript(segment);
          dispatch(saveWhatsappCallTranscriptSegment(callId, segment));
        } catch (error) {
          console.error('Error handling WhatsApp transcript event', error);
        }
      };

      socket.onerror = () => {
        setRecordingError('Recording is on. Live transcript had a connection issue.');
      };

      transcriberRefs.current[speaker] = { socket, context, source, processor, silenceGain };
      context.resume?.().catch(() => {});
    } catch (error) {
      console.error('Error starting WhatsApp live transcriber', error);
      setRecordingError('Recording is on. Live transcript is unavailable.');
    }
  }, [appendTranscript, dispatch]);

  const stopRecording = React.useCallback((errorMessage = '', options = {}) => new Promise((resolve) => {
    const { waitForUpload = true } = options || {};
    const { current } = recordingRef;
    if (!current?.recorder || !current.recordingId) {
      cleanupRecordingAudio();
      stopAllTranscribers();
      resolve(null);
      return;
    }
    if (current.stopping) {
      resolve(null);
      return;
    }
    current.stopping = true;

    setRecordingState('stopping');
    stopAllTranscribers();

    const resetRecorderRef = () => {
      cleanupRecordingAudio();
      recordingRef.current = {
        recorder: null,
        chunks: [],
        sequence: 0,
        callId: null,
        recordingId: null,
        mimeType: '',
        startedAt: null,
        stopping: false,
      };
      setRecordingState(errorMessage ? 'failed' : 'ready');
    };

    const finish = () => {
      const durationMs = current.startedAt ? Date.now() - current.startedAt : recordingElapsedMs;
      const finalBlob = current.chunks.length
        ? new Blob(current.chunks, { type: current.mimeType || 'audio/webm' })
        : null;
      const transcriptText = transcriptRows.map((item) => `${item.speaker || 'unknown'}: ${item.text}`).join('\n');
      const completePromise = Promise.resolve(dispatch(completeWhatsappCallRecording(current.callId, current.recordingId, {
        blob: finalBlob,
        durationMs,
        mimeType: current.mimeType || finalBlob?.type,
        transcriptText,
        errorMessage,
      })));
      resetRecorderRef();
      if (waitForUpload) {
        completePromise
          .then(resolve)
          .catch((error) => {
            console.error('Error completing WhatsApp call recording', error);
            resolve(null);
          });
      } else {
        completePromise.catch((error) => {
          console.error('Error completing WhatsApp call recording', error);
        });
        resolve(null);
      }
    };

    try {
      current.recorder.onstop = finish;
      if (current.recorder.state !== 'inactive') {
        current.recorder.stop();
      } else {
        finish();
      }
    } catch (error) {
      console.error('Error stopping WhatsApp call recording', error);
      resetRecorderRef();
      resolve(null);
    }
  }), [cleanupRecordingAudio, dispatch, recordingElapsedMs, stopAllTranscribers, transcriptRows]);

  const startRecording = React.useCallback(async (callId) => {
    if (!callId || !hasRecordingSupport || !localStreamRef.current || recordingRef.current.recordingId) return;
    try {
      const response = await dispatch(startWhatsappCallRecording(callId));
      const recording = response?.data;
      if (!response?.ok || !recording?.id) {
        setRecordingError(getResponseError(response, 'Call connected. Recording could not start.'));
        setRecordingState('failed');
        return;
      }

      const mixedStream = connectStreamToRecording(localStreamRef.current, 'agent');
      if (remoteStreamRef.current) connectStreamToRecording(remoteStreamRef.current, 'patient');
      const mimeType = getRecordingMimeType();
      const recorder = new MediaRecorder(mixedStream, mimeType ? { mimeType } : undefined);
      const startedAt = Date.now();

      recordingRef.current = {
        recorder,
        chunks: [],
        sequence: 0,
        callId,
        recordingId: recording.id,
        mimeType: mimeType || recorder.mimeType || 'audio/webm',
        startedAt,
        stopping: false,
      };

      recorder.ondataavailable = (event) => {
        if (!event.data?.size) return;
        const { sequence } = recordingRef.current;
        recordingRef.current.sequence += 1;
        recordingRef.current.chunks.push(event.data);
        dispatch(uploadWhatsappCallRecordingChunk(callId, recording.id, {
          blob: event.data,
          sequence,
          durationMs: Date.now() - startedAt,
          mimeType: recordingRef.current.mimeType,
        }));
      };

      recorder.onerror = () => {
        setRecordingError('Recording had an issue. The call is still active.');
      };

      recorder.start(5000);
      setRecordingError('');
      setRecordingElapsedMs(0);
      setRecordingState('recording');
      setShowTranscript(true);
      startTranscriber(callId, localStreamRef.current, 'agent', recording.id);
      if (remoteStreamRef.current) {
        startTranscriber(callId, remoteStreamRef.current, 'patient', recording.id);
      }
    } catch (error) {
      console.error('Error starting WhatsApp call recording', error);
      setRecordingError('Call connected. Recording could not start.');
      setRecordingState('failed');
    }
  }, [connectStreamToRecording, dispatch, hasRecordingSupport, startTranscriber]);

  const closePeer = React.useCallback(() => {
    try {
      if (peerRef.current) {
        peerRef.current.ontrack = null;
        peerRef.current.onicecandidate = null;
        peerRef.current.close();
      }
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioRef.current) {
        audioRef.current.srcObject = null;
      }
      stopAllTranscribers();
    } catch (error) {
      console.error('Error closing WhatsApp call peer connection', error);
    } finally {
      peerRef.current = null;
      localStreamRef.current = null;
      remoteStreamRef.current = null;
      setMicActive(false);
    }
  }, [stopAllTranscribers]);

  const createPeerConnection = React.useCallback(async () => {
    if (!hasWebRtc) {
      throw new Error('This browser does not support WhatsApp calling.');
    }

    closePeer();
    const localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    const peer = new window.RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });

    localStream.getTracks().forEach((track) => peer.addTrack(track, localStream));
    peer.ontrack = (event) => {
      const [remoteStream] = event.streams || [];
      if (audioRef.current && remoteStream) {
        remoteStreamRef.current = remoteStream;
        connectStreamToRecording(remoteStream, 'patient');
        if (recordingRef.current.recordingId && recordingRef.current.callId) {
          startTranscriber(recordingRef.current.callId, remoteStream, 'patient', recordingRef.current.recordingId);
        }
        audioRef.current.srcObject = remoteStream;
        audioRef.current.play?.().catch(() => {});
      }
    };

    localStreamRef.current = localStream;
    peerRef.current = peer;
    setMicActive(true);
    return peer;
  }, [closePeer, connectStreamToRecording, hasWebRtc, startTranscriber]);

  const surfaceError = React.useCallback((message) => {
    setLocalError(message);
    dispatch(updateError({ success: false, message }));
  }, [dispatch]);

  // A failed getUserMedia is the user's hardware, not our software, but it
  // arrived here as a raw DOMException and surfaceError passed .message
  // straight through: the staff member saw the browser's own wording
  // ("Requested device not found"), which tells them nothing actionable, and
  // errorHandler.jsx captured it to Sentry as an application fault
  // (JAVASCRIPT-REACT-1BF). It captures because updateError carries no HTTP
  // status, and errorHandler treats a status-less error as a network failure
  // worth reporting.
  //
  // So: translate the DOMException into something the user can act on, and
  // dispatch it with severity 'warning' — errorHandler's existing opt-out
  // (errorHandler.jsx:165-170). The toast still shows; the capture does not run.
  const surfaceDeviceWarning = React.useCallback((message) => {
    setLocalError(message);
    dispatch(updateError({ success: false, severity: 'warning', message }));
  }, [dispatch]);

  const handleCallError = React.useCallback((error, fallback) => {
    const advice = describeMediaDeviceError(error);
    if (advice) {
      surfaceDeviceWarning(advice);
      return;
    }
    surfaceError(error?.message || fallback);
  }, [surfaceDeviceWarning, surfaceError]);

  React.useEffect(() => {
    if (canUseCalling && currentBusinessId && currentBusinessId !== -1 && currentWhatsappPhoneId && currentWhatsappPhoneId !== -1) {
      dispatch(fetchWhatsappCallingSettings());
    }
  }, [canUseCalling, currentBusinessId, currentWhatsappPhoneId, dispatch]);

  React.useEffect(() => {
    setPermissionRequired(false);
    setLocalError('');
  }, [contactWaId]);

  React.useEffect(() => {
    const callId = activeCall?.id;
    const isTerminal = callId && TERMINAL_STATUSES.has(activeCall?.status);

    if (callId && !isTerminal) {
      lastActiveCallIdRef.current = callId;
      return undefined;
    }

    const hadActiveCall = Boolean(lastActiveCallIdRef.current);
    const hasLocalCallResources = Boolean(
      recordingRef.current?.recordingId ||
      peerRef.current ||
      localStreamRef.current ||
      Object.keys(transcriberRefs.current || {}).length
    );
    if (!hadActiveCall && !hasLocalCallResources) {
      return undefined;
    }

    lastActiveCallIdRef.current = null;
    stopRecording('', { waitForUpload: false }).finally(closePeer);
    return undefined;
  }, [activeCall?.id, activeCall?.status, closePeer, stopRecording]);

  React.useEffect(() => {
    if (!activeCall?.id) {
      resetRecordingUi();
    }
  }, [activeCall?.id, resetRecordingUi]);

  React.useEffect(() => {
    if (!activeCall?.id) return;
    dispatch(fetchWhatsappCallTranscript(activeCall.id));
  }, [activeCall?.id, dispatch]);

  React.useEffect(() => {
    if (activeCall?.id && activeCall?.status === 'accepted' && micActive && recordingState === 'idle') {
      startRecording(activeCall.id);
    }
  }, [activeCall?.id, activeCall?.status, micActive, recordingState, startRecording]);

  React.useEffect(() => {
    if (!isRecording) return undefined;
    const timer = setInterval(() => {
      const { startedAt } = recordingRef.current;
      if (startedAt) setRecordingElapsedMs(Date.now() - startedAt);
    }, 1000);
    return () => clearInterval(timer);
  }, [isRecording]);

  React.useEffect(() => {
    if (!activeCall?.id || activeCall?.remote_sdp || TERMINAL_STATUSES.has(activeCall?.status)) return undefined;
    dispatch(fetchWhatsappCallDetail(activeCall.id));
    const detailPollId = setInterval(() => {
      dispatch(fetchWhatsappCallDetail(activeCall.id));
    }, 1500);
    return () => clearInterval(detailPollId);
  }, [activeCall?.id, activeCall?.remote_sdp, activeCall?.status, dispatch]);

  React.useEffect(() => {
    const peer = peerRef.current;
    if (!peer || !activeCall?.id || !activeCall?.remote_sdp || activeCall?.direction !== 'outbound') return;
    if (remoteDescriptionRef.current[activeCall.id] === activeCall.remote_sdp) return;
    peer.setRemoteDescription({ type: 'answer', sdp: activeCall.remote_sdp })
      .then(() => {
        remoteDescriptionRef.current[activeCall.id] = activeCall.remote_sdp;
      })
      .catch((error) => {
        console.error('Error applying WhatsApp call remote SDP', error);
        surfaceError('Failed to connect WhatsApp call audio.');
      });
  }, [activeCall?.direction, activeCall?.id, activeCall?.remote_sdp, surfaceError]);

  const handleStartCall = async () => {
    setLocalError('');
    setPermissionRequired(false);
    resetRecordingUi();
    if (!hasWebRtc) {
      surfaceError('WhatsApp calling requires a browser with microphone and WebRTC support.');
      return;
    }
    if (isCallingConfiguredOff) {
      surfaceError('WhatsApp calling is disabled for this phone number.');
      return;
    }
    try {
      const peer = await createPeerConnection();
      const offer = await peer.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: false });
      await peer.setLocalDescription(offer);
      const response = await dispatch(connectWhatsappCall({
        to: contactWaId,
        sdp: offer.sdp,
        clientCallId: buildClientCallId(),
        leadId: contact?.leadId,
        threadId: contact?.threadId,
      }));
      if (response?.status === 409 && response?.data?.code === 'call_permission_required') {
        closePeer();
        setPermissionRequired(true);
        return;
      }
      if (!response?.ok) {
        closePeer();
        surfaceError(response?.data?.error || response?.data?.detail || 'Failed to start WhatsApp call.');
      }
    } catch (error) {
      console.error('Error starting WhatsApp call', error);
      closePeer();
      handleCallError(error, 'Failed to start WhatsApp call.');
    }
  };

  const handleAnswerCall = async () => {
    if (!activeCall?.id) return;
    setLocalError('');
    resetRecordingUi();
    try {
      let callDetail = activeCall;
      if (!callDetail?.remote_sdp) {
        const detailResponse = await dispatch(fetchWhatsappCallDetail(activeCall.id));
        callDetail = detailResponse?.data ?? callDetail;
      }
      if (!callDetail?.remote_sdp) {
        surfaceError('Incoming WhatsApp call is missing the remote audio offer.');
        return;
      }

      const peer = await createPeerConnection();
      await peer.setRemoteDescription({ type: 'offer', sdp: callDetail.remote_sdp });
      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);
      await dispatch(preAcceptWhatsappCall(activeCall.id, answer.sdp));
      const response = await dispatch(acceptWhatsappCall(activeCall.id, answer.sdp));
      if (!response?.ok) {
        closePeer();
        surfaceError(response?.data?.error || response?.data?.detail || 'Failed to answer WhatsApp call.');
      }
    } catch (error) {
      console.error('Error answering WhatsApp call', error);
      closePeer();
      handleCallError(error, 'Failed to answer WhatsApp call.');
    }
  };

  const handleRejectCall = async () => {
    if (!activeCall?.id) return;
    const callId = activeCall.id;
    stopRecording('Call declined before recording completed.', { waitForUpload: false });
    const response = await dispatch(rejectWhatsappCall(callId));
    closePeer();
    if (response?.ok) {
      dispatch(clearWhatsappActiveCall());
    }
  };

  const handleEndCall = async () => {
    if (!activeCall?.id) return;
    const callId = activeCall.id;
    stopRecording('', { waitForUpload: false });
    const response = await dispatch(terminateWhatsappCall(callId));
    closePeer();
    if (response?.ok) {
      dispatch(clearWhatsappActiveCall());
    }
  };

  const handleRequestPermission = async () => {
    if (!contactWaId) return;
    await dispatch(sendWhatsappCallPermissionRequest(contactWaId));
    setPermissionRequired(false);
  };

  if (!canUseCalling) return null;

  const showPanel = Boolean(activeCall?.id || permissionRequired || localError || whatsappCallError);
  const displayStatus = activeStatus === 'connecting' && activeCall?.remote_sdp ? 'accepted' : activeStatus;
  const statusLabel = STATUS_LABELS[displayStatus] || displayStatus || 'Call';
  const peerLabel = activeCall?.peer_waid || contactWaId;

  return (
    <>
      <Tooltip
        title={
          isCallingConfiguredOff
            ? 'WhatsApp calling is disabled for this phone number'
            : 'Start WhatsApp call'
        }
        arrow
      >
        <span>
          <IconButton
            onClick={handleStartCall}
            disabled={buttonDisabled}
            sx={iconSx}
            aria-label="Start WhatsApp call"
          >
            {callingSettingsLoading || whatsappCallActionLoading ? (
              <CircularProgress size={16} />
            ) : (
              <Icon icon="solar:phone-calling-rounded-outline" width={18} height={18} />
            )}
          </IconButton>
        </span>
      </Tooltip>

      {showPanel && (
        <Box
          sx={{
            position: 'absolute',
            top: 66,
            left: 16,
            right: 16,
            zIndex: 18,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: '10px',
            backgroundColor: COLORS.paper,
            border: `1px solid ${COLORS.dividerLight}`,
            boxShadow: '0 8px 24px rgba(33, 43, 54, 0.12)',
          }}
        >
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio ref={audioRef} autoPlay />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: micActive ? COLORS.primaryLight : COLORS.dividerLighter,
                  color: micActive ? COLORS.primaryDark : COLORS.textSecondary,
                  flexShrink: 0,
                }}
              >
                <Icon icon={micActive ? 'solar:microphone-3-bold' : 'solar:phone-calling-rounded-outline'} width={16} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.textPrimary, lineHeight: 1.25 }}>
                  {permissionRequired ? 'Call permission required' : statusLabel}
                </Typography>
                <Typography sx={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.25 }} noWrap>
                  {permissionRequired
                    ? `Ask ${contact?.name || peerLabel} to allow calls on WhatsApp`
                    : `${activeCall?.direction === 'inbound' ? 'Incoming from' : 'Calling'} ${contact?.name || peerLabel}`}
                </Typography>
                {(isRecording || recordingState === 'ready' || recordingError) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4, minWidth: 0 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: recordingError ? COLORS.warning : COLORS.error, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 11, color: recordingError ? COLORS.warningDark : COLORS.textSecondary, lineHeight: 1.2 }} noWrap>
                      {recordingError || (isRecording ? `Recording ${formatDuration(recordingElapsedMs)}` : 'Recording saved')}
                    </Typography>
                  </Box>
                )}
                {(localError || whatsappCallError) && (
                  <Typography sx={{ fontSize: 11, color: COLORS.errorDark, mt: 0.25 }} noWrap>
                    {localError || whatsappCallError}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
              {activeCall?.id && (
                <Tooltip title="Live transcript" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setShowTranscript((value) => !value)}
                    sx={{
                      width: 30,
                      height: 30,
                      color: showTranscript ? COLORS.primary : COLORS.textSecondary,
                      backgroundColor: showTranscript ? COLORS.primaryLighter : 'transparent',
                    }}
                    aria-label="Toggle call transcript"
                  >
                    <Icon icon="solar:document-text-outline" width={16} />
                  </IconButton>
                </Tooltip>
              )}
            {permissionRequired && (
              <Button
                size="small"
                variant="contained"
                onClick={handleRequestPermission}
                disabled={whatsappCallActionLoading}
                sx={{ textTransform: 'none', backgroundColor: COLORS.primary, '&:hover': { backgroundColor: COLORS.primaryDark } }}
              >
                Request
              </Button>
            )}
            {isIncomingCall && (
              <Button
                size="small"
                variant="contained"
                onClick={handleAnswerCall}
                disabled={whatsappCallActionLoading}
                sx={{ textTransform: 'none', backgroundColor: COLORS.primary, '&:hover': { backgroundColor: COLORS.primaryDark } }}
              >
                Answer
              </Button>
            )}
            {isIncomingCall && (
              <Button size="small" variant="outlined" color="error" onClick={handleRejectCall} disabled={whatsappCallActionLoading} sx={{ textTransform: 'none' }}>
                Decline
              </Button>
            )}
            {activeCall?.id && !isIncomingCall && !TERMINAL_STATUSES.has(activeStatus) && (
              <Button size="small" variant="outlined" color="error" onClick={handleEndCall} disabled={whatsappCallActionLoading} sx={{ textTransform: 'none' }}>
                End
              </Button>
            )}
            {(TERMINAL_STATUSES.has(activeStatus) || permissionRequired || localError || whatsappCallError) && (
              <IconButton
                size="small"
                onClick={() => {
                  setLocalError('');
                  setPermissionRequired(false);
                  dispatch(clearWhatsappActiveCall());
                }}
                sx={{ width: 28, height: 28, color: COLORS.textSecondary }}
                aria-label="Dismiss call status"
              >
                <Icon icon="mdi:close" width={16} />
              </IconButton>
            )}
            </Box>
          </Box>
          {showTranscript && activeCall?.id && (
            <Box
              sx={{
                borderTop: `1px solid ${COLORS.dividerLight}`,
                pt: 1,
                maxHeight: 172,
                overflowY: 'auto',
              }}
            >
              {transcriptRows.length === 0 && !partialTranscript.agent && !partialTranscript.patient ? (
                <Typography sx={{ fontSize: 12, color: COLORS.textSecondary }}>
                  Live transcript will appear here.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {transcriptRows.map((item) => (
                    <Box key={item.id || item.provider_event_id || item.providerEventId || `${item.speaker}-${item.text}`} sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: COLORS.textSecondary, textTransform: 'uppercase', lineHeight: 1.3 }}>
                        {getSpeakerLabel(item.speaker, contact?.name || 'Patient')}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: COLORS.textPrimary, lineHeight: 1.45 }}>
                        {item.text}
                      </Typography>
                    </Box>
                  ))}
                  {Object.entries(partialTranscript).map(([speaker, text]) => text ? (
                    <Box key={`partial-${speaker}`} sx={{ minWidth: 0, opacity: 0.7 }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: COLORS.textSecondary, textTransform: 'uppercase', lineHeight: 1.3 }}>
                        {getSpeakerLabel(speaker, contact?.name || 'Patient')}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.45 }}>
                        {text}
                      </Typography>
                    </Box>
                  ) : null)}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </>
  );
}

WhatsappCallControls.propTypes = {
  contact: PropTypes.shape({
    phoneNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    threadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  disabled: PropTypes.bool,
  iconSx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

WhatsappCallControls.defaultProps = {
  contact: {},
  disabled: false,
  iconSx: {},
};
